import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  TextField,
  Alert,
  Divider,
} from '@mui/material';
import api from '../api';

export default function AiSettings() {
  const [apiKey, setApiKey] = useState('');
  const [configured, setConfigured] = useState(null); // null=加载中
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/api/ai/config');
        setConfigured(res.data.configured);
      } catch {
        setConfigured(false);
      }
    })();
    // 老版本曾把 Key 存在浏览器 localStorage，服务器端方案上线后不再需要，顺手清理
    localStorage.removeItem('qwen_api_key');
  }, []);

  const handleSave = async () => {
    if (!apiKey || apiKey.trim().length < 10) {
      setMessage({ type: 'error', text: '请输入完整的 API Key' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const res = await api.post('/api/ai/config', { apiKey: apiKey.trim() });
      setConfigured(true);
      setApiKey('');
      setMessage({ type: 'success', text: res.data.message || 'API Key 已保存到服务器' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || '保存失败：' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage(null);
    setTestResult(null);
    try {
      // 不带任何 Key 头：验证的就是服务器端配置的 Key
      const response = await api.post('/api/ai/parse-quotation',
        { text: '太龙苑小区电缆线路更换工程，线路一：铜芯铠装电缆4×16mm² 180米 61.95元/米' }
      );
      setTestResult(response.data);
      setMessage({ type: 'success', text: 'AI 测试成功！' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || '测试失败: ' + error.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>AI 设置</Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>AI 模型配置（阿里云百炼）</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            把阿里云百炼的 API Key 保存到<strong>服务器</strong>（加密存储）：<strong>一次配置，手机、电脑所有设备都能直接用
            「AI 解析」</strong>，不需要每台设备单独设置。默认模型 DeepSeek V4 Flash 0731。
          </Typography>

          {configured !== null && (
            <Alert severity={configured ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {configured
                ? '✅ 服务器已配置 API Key，所有设备可直接使用 AI 解析。'
                : '尚未配置：粘贴 Key 保存后，全设备即可使用。'}
            </Alert>
          )}

          <TextField
            fullWidth
            label="API Key（sk- 开头）"
            variant="outlined"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="REDACTED-API-KEY"
            type="password"
            helperText="出于安全，已保存的 Key 不会回显；换 Key 直接粘贴保存即可覆盖。"
            sx={{ mb: 2 }}
          />

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || !apiKey.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? '保存中...' : '保存到服务器'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleTest}
              disabled={testing || !configured}
              startIcon={testing ? <CircularProgress size={20} /> : null}
            >
              {testing ? '测试中...' : '测试连接'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {testResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>测试结果</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" gutterBottom>
              <strong>项目名称：</strong>{testResult.projectName}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>建设单位：</strong>{testResult.buildUnit}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>承包方式：</strong>{testResult.contractMethod}
            </Typography>
            {testResult.sections && testResult.sections.length > 0 && (
              <>
                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                  <strong>识别到 {testResult.sections.length} 个分项：</strong>
                </Typography>
                {testResult.sections.map((section, index) => (
                  <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                    • {section.name}（{section.items?.length || 0} 个明细行）
                  </Typography>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>如何获取API Key</Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" paragraph>
            1. 访问阿里云百炼控制台，用你的阿里云账号登录
          </Typography>
          <Typography variant="body2" paragraph>
            2. 开通「模型服务」，在 API-KEY 管理页创建一个 Key（sk- 开头）
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Key 加密保存在你自己的服务器数据库里，仅管理员可更换，任何接口都不会把它发回去。
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
